import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import {
  AddToGroupButton,
  AiSuggestionsButtonWrapper,
  AiSuggestionsMenu,
  AiSuggestionsMenuItem,
  AlertButton,
  Avatar,
  ClearMediaText,
  CloseButton,
  Content,
  DateDivider,
  DateDividerLine,
  DotText,
  Footer,
  FooterButton,
  FooterContent,
  Header,
  HeaderChatInfoRow,
  HeaderColumn,
  HeaderRow,
  HeaderText,
  HeaderTextBold,
  InfiniteScrollMarker,
  Line,
  LoaderContainer,
  LoaderMoreContainer,
  MediaFooter,
  MediaWrapper,
  ChatterAvatar,
  ChatterAvatarTooltip,
  ChatterAvatarWrapper,
  MessageBubble,
  MessageBubbleWrapper,
  MessageRow,
  MessageSpinner,
  MessageTimeText,
  NewMessagesContainer,
  PageContainer,
  PinWrapper,
  PlayIconWrapper,
  PriceInput,
  SelectedMediaContainer,
  TimeText,
  VipButton,
  VoiceResponseText,
} from './ChatPage.styles';
import * as yup from 'yup';
import { formatCurrency, formatPrice, getInitials, objectToFormData } from '../../../utils/util';
import { Text12ExtraBold, Text14Light, Text16Bold } from '../../../components/utils/Text/Text.styles';
import { ReactComponent as EmptyPinIcon } from '../../../assets/icons/empty-pin.svg';
import { ReactComponent as PinIcon } from '../../../assets/icons/pin.svg';
import { ReactComponent as CurrencyDollarIcon } from '../../../assets/icons/currency-dollar-circle.svg';
import { ReactComponent as MicrophoneIcon } from '../../../assets/icons/microphone-01.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as SendIcon } from '../../../assets/icons/send.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as CheckEmptyIcon } from '../../../assets/icons/check-empty.svg';
import { ReactComponent as UserPlusIcon } from '../../../assets/icons/user-plus.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play.svg';
import { ReactComponent as PlayWhiteIcon } from '../../../assets/icons/play-white.svg';
import { ReactComponent as PauseIcon } from '../../../assets/icons/pause-circle.svg';
import { ReactComponent as StarsIcon } from '../../../assets/icons/stars.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../assets/icons/chevron_left.svg';
import { ReactComponent as AlertHexagonIcon } from '../../../assets/icons/alert-hexagon.svg';
import SpinnerGIF from '../../../assets/gifs/spinner.gif';
import {
  AddUserToCollectionModal,
  AiSuggestions,
  Bundle,
  CenteredBubble,
  EditChatDisplayNameModal,
  Image,
  Input,
  ReportMessageModal,
  SecretModal,
  SelectBundleModal,
  SelectMediaModal,
  Spinner,
  Switch,
  UploadMediaButtonAlt,
  Video,
} from '../../../components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCollectionsActionsPending,
  selectFetchInfluencerChatInfo,
  selectUserInfluencer,
  setActiveUserInfluencerId,
} from '../../../store/slices/collections/slice';
import {
  fetchInfluencerChat,
  getAiChatSuggestions,
  markChatAsRead,
  pinUser,
  sendMessage,
  toggleUserInfluencerManualChat,
  unblockUserInfluencerChat,
} from '../../../store/slices/collections/asyncThunks';
import {
  ALBUM_MEDIA_TYPE,
  CHAT_ACTIVITY_TYPE,
  CRM_ORGANIZATION_MEMBER_ROLE,
  MANUAL_FLOW_INSTRUCTION_TYPE,
  MEDIA_ASSET_SALE_TAG,
  MESSAGE_FLOW,
  MESSAGE_TARGET,
  MESSAGE_TYPE,
  SOCKET_EVENTS,
  UPLOAD_FILE_CONSTRAINTS,
} from '../../../utils/constants';
import { format } from 'date-fns';
import { notifyError } from '../../../utils/notify';
import UploadChatMediaButton from '../../../components/chat/UploadChatMediaButton/UploadChatMediaButton';
import MediaItem from '../../../components/MediaItem/MediaItem';
import {
  fetchBundleById,
  testVoice,
  uploadAssistedPhoto,
  uploadAssistedVideo,
} from '../../../store/slices/user/asyncThunks';
import { selectUser, selectUserActionsPending } from '../../../store/slices/user/slice';
import { useMediaQuery } from 'react-responsive';
import { useChatDraft } from '../../../hooks/useChatDraft';
import PreviewAlbumMediaModal from '../../../components/modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';
import { useSocketEvent } from '../../../hooks/useSocketEvent';
import { useChatRoom } from '../../../hooks/useChatRoom';

const sendMessageFormSchema = yup.object({
  mediaTags: yup.array().of(yup.string()).default([]),
  message: yup.string().when('mediaTags', {
    is: tags => !tags || tags.length === 0,
    then: s => s.required('Message is required.'),
    otherwise: s => s.optional(),
  }),
  price: yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .when('mediaTags', {
      is: tags => tags?.includes('SINGLE_PAID'),
      then: n =>
        n.typeError('Price must be a number').required('Price is required.').min(0, 'Price must be 0 or greater'),
      otherwise: n => n.optional(),
    }),
  media: yup.array().when('mediaTags', {
    is: tags => tags?.includes('SINGLE_FREE'),
    then: arr => arr.min(1, 'You must upload exactly 1 media item').max(1, 'Only one free media item is allowed.'),
    otherwise: arr =>
      arr.when('mediaTags', {
        is: tags => tags?.includes('SINGLE_PAID'),
        then: arr => arr.min(1, 'You must upload at least 1 media item').max(10, 'You can upload up to 10 items only'),
        otherwise: arr => arr.optional(),
      }),
  }),
});

const SECRET_ACTION = {
  UNBLOCK: 'UNBLOCK',
  AI_CHAT: 'AI_CHAT',
};

const createAudioFromVoiceBuffer = voiceBuffer => {
  const audioBlob = new Blob([new Uint8Array(voiceBuffer)], { type: 'audio/mp3' });

  return new Audio(URL.createObjectURL(audioBlob));
};

const ChatPage = () => {
  const dispatch = useDispatch();
  const { chatId, influencerId } = useParams();
  const [searchParams] = useSearchParams();
  const timestamp = searchParams.get('timestamp');
  const interactionId = searchParams.get('interactionId');
  const [setShowContent, handleRefreshMessagesState] = useOutletContext();
  const userInfluencer = useSelector(selectUserInfluencer);
  const chatContainerRef = useRef(null);
  const loadMoreRef = useRef();
  const initialScrollDoneRef = useRef(false);
  const selectMediaModalRef = useRef();
  const selectBundleModalRef = useRef();
  const addUserToCollectionModalRef = useRef();
  const editChatNameModalRef = useRef();
  const secretModalRef = useRef();
  const previewMediaModalRef = useRef();
  const reportMessageModalRef = useRef();
  const [reportedInteractionId, setReportedInteractionId] = useState(null);
  const { data, hasMore, nextCursor, pending, fetchMorePending } = useSelector(selectFetchInfluencerChatInfo);
  const {
    pinUser: pinUserPending,
    toggleUserInfluencerManualChat: toggleUserInfluencerManualChatPending,
    getAiChatSuggestions: getAiChatSuggestionsPending,
    unblockUserInfluencerChat: unblockUserInfluencerChatPending,
  } = useSelector(selectCollectionsActionsPending);
  const user = useSelector(selectUser);
  const isReviewer = user.role === CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER;
  const canSeeChatterName = [
    CRM_ORGANIZATION_MEMBER_ROLE.OWNER,
    CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR,
    CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD,
    CRM_ORGANIZATION_MEMBER_ROLE.REVIEWER,
  ].includes(user.role);
  const { testVoice: testVoicePending } = useSelector(selectUserActionsPending);
  const [footerHeight, setFooterHeight] = useState(0);
  const [progress, setProgress] = useState(0);
  const [footerRef, setFooterRef] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [bundleId, setBundleId] = useState(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [voiceBuffer, setVoiceBuffer] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [reload, setReload] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [manualChatEnabled, setManualChatEnabled] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [secretAction, setSecretAction] = useState(null);
  const smallScreen = useMediaQuery({ minWidth: 0, maxWidth: 768 });
  const [isAiSuggestion, setIsAiSuggestion] = useState(false);

  const stateRef = useRef({
    chatId,
    isAtBottom: true,
    influencerId,
  });

  useChatRoom(chatId);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    delayError: 300,
    defaultValues: {
      message: '',
      price: '',
      freePreviewMedia: null,
      media: [],
      mediaTags: [],
      isVip: false,
    },
    resolver: yupResolver(sendMessageFormSchema),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'media' });

  const messageValue = watch('message');
  const freePreviewMediaWatch = watch('freePreviewMedia');
  const mediaTags = watch('mediaTags');

  const scrollToBottom = useCallback(
    refreshState => {
      const { chatId, influencerId: infId } = stateRef.current;
      const container = chatContainerRef.current;
      if (!container) return;
      if (refreshState) {
        dispatch(fetchInfluencerChat({ userInfluencerId: chatId, ...(infId && { influencerId: infId }) }))
          .unwrap()
          .then(() => {
            chatContainerRef.current.scrollTop = chatContainerRef.current?.scrollHeight || 0;
          })
          .catch(() => {});
      }
      container.scrollTop = container?.scrollHeight;
      setIsAtBottom(true);
      setNewMessagesCount(0);
    },
    [dispatch],
  );

  useEffect(() => {
    if (userInfluencer) {
      setManualChatEnabled(userInfluencer.manualChat?.enabled);
    }
  }, [userInfluencer]);

  useEffect(() => {
    if (!messageValue?.trim()) {
      setIsAiSuggestion(false);
    }
  }, [messageValue]);

  useEffect(() => {
    initialScrollDoneRef.current = false;
    dispatch(setActiveUserInfluencerId(chatId));
    dispatch(
      fetchInfluencerChat({
        userInfluencerId: chatId,
        ...(timestamp && { cursor: timestamp, bidirectional: true }),
        ...(influencerId && { influencerId }),
      }),
    )
      .unwrap()
      .then(() => {
        setTimeout(() => {
          if (interactionId) {
            const target = document.getElementById(`received-${interactionId}`);
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              initialScrollDoneRef.current = true;
              return;
            }
          }
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
          initialScrollDoneRef.current = true;
        }, 100);
      })
      .catch(() => {
        initialScrollDoneRef.current = true;
      });
  }, [dispatch, chatId, influencerId, timestamp, interactionId]);

  useEffect(() => {
    if (!footerRef) return;

    const observer = new ResizeObserver(() => {
      setFooterHeight(footerRef.offsetHeight);
    });

    observer.observe(footerRef);

    return () => observer.disconnect();
  }, [footerRef]);

  const onProgress = progress => {
    setProgress(progress > 99 ? 99 : progress.toFixed(1));
  };

  const { clearDraft } = useChatDraft(chatId, { watch, setValue, reset });

  const sendChatMessage = async (data, overrideGrammarCheck) => {
    const body = { overrideGrammarCheck };
    setAiSuggestions(null);

    scrollToBottom();
    if (!data.mediaTags.length) {
      body.text = data.message;
      reset();
      clearDraft();

      if (voiceBuffer) {
        setSendingMessage(true);
        if (audioRef.current) {
          audioRef.current.pause();
        }
        if (aiSuggestionAudioRef.current) {
          aiSuggestionAudioRef.current.pause();
        }

        body.voiceBuffer = new Uint8Array(voiceBuffer).buffer;
      }
    } else {
      setSendingMessage(true);
      if (data.message) {
        body.text = data.message;
      }
      if (data.mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)) {
        const freePreviewMedia = data.freePreviewMedia;

        let representativeFilename = null;
        let representativeType = null;
        if (freePreviewMedia) {
          if (!freePreviewMedia.url.startsWith('https://')) {
            const freePreviewType = freePreviewMedia.file.type.includes('video')
              ? ALBUM_MEDIA_TYPE.VIDEO
              : ALBUM_MEDIA_TYPE.PHOTO;
            representativeType = freePreviewType;
            if (freePreviewType === ALBUM_MEDIA_TYPE.PHOTO) {
              const response = await dispatch(
                uploadAssistedPhoto({
                  photo: freePreviewMedia,
                  type: MEDIA_ASSET_SALE_TAG.BUNDLE,
                  representativeMedia: true,
                  onProgress,
                  influencerId,
                }),
              ).unwrap();

              representativeFilename = response.fileName;
            } else {
              const response = await dispatch(
                uploadAssistedVideo({
                  video: freePreviewMedia,
                  type: MEDIA_ASSET_SALE_TAG.BUNDLE,
                  representativeMedia: true,
                  onProgress,
                  influencerId,
                }),
              ).unwrap();
              representativeFilename = response.fileName;
            }
            body.representativeMediaType = representativeType;
            body.representativeMediaPath = representativeFilename;
          } else {
            body.representativeMediaType = freePreviewMedia.file.type;
            body.representativeMediaPath = freePreviewMedia.file.path;
          }
        }
        body.cost = data.price;
      }

      const albumMediaIds = data.media.map(m => m.id);
      if (bundleId) {
        body.albumMediaIds = [bundleId];
      } else {
        body.albumMediaIds = albumMediaIds;
      }

      if (body.albumMediaIds && (body.albumMediaIds.length > 1 || bundleId) && data.isVip) {
        body.isVip = data.isVip;
      }
    }

    const formData = objectToFormData(body);
    formData.append('isAiSuggestion', String(isAiSuggestion));

    dispatch(
      sendMessage({
        userInfluencerId: chatId,
        body: formData,
        ...(influencerId && { influencerId }),
      }),
    )
      .unwrap()
      .then(({ correctedText }) => {
        if (correctedText) {
          setAiSuggestions([{ message: correctedText }, { message: body.text, isOriginal: true }]);
          return;
        }
        if (data.mediaTags.length) {
          reset();
          clearDraft();
        }

        setIsAiSuggestion(false);
        setVoiceBuffer(null);
      })
      .catch(err => {
        setProgress(0);
        notifyError(err.message);
      })
      .finally(() => {
        setSendingMessage(false);
      });
  };

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (timestamp) return;
      if (!initialScrollDoneRef.current) return;
      if (entry.isIntersecting && !pending && !fetchMorePending && hasMore && nextCursor) {
        const previousScrollHeight = chatContainerRef.current?.scrollHeight || 0;
        dispatch(
          fetchInfluencerChat({
            cursor: nextCursor,
            userInfluencerId: chatId,
            ...(influencerId && { influencerId }),
          }),
        )
          .unwrap()
          .then(() => {
            chatContainerRef.current.scrollTop = chatContainerRef.current?.scrollHeight - previousScrollHeight;
          });
      }
    },
    [pending, hasMore, nextCursor, fetchMorePending, dispatch, chatId, influencerId, timestamp],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 1.0 });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  useEffect(() => {
    stateRef.current = {
      chatId,
      isAtBottom,
      influencerId,
    };
  }, [isAtBottom, chatId, influencerId]);

  useEffect(() => {
    selectMediaModalRef.current?.hide();
    selectBundleModalRef.current?.hide();
    addUserToCollectionModalRef.current?.hide();
    editChatNameModalRef.current?.hide();
    secretModalRef.current?.hide();
    previewMediaModalRef.current?.hide();
    reportMessageModalRef.current?.hide();
    setReportedInteractionId(null);
  }, [chatId]);

  const handleRefreshState = useCallback(() => {
    const { isAtBottom, chatId, influencerId: infId } = stateRef.current;

    if (!isAtBottom) {
      setNewMessagesCount(prev => prev + 1);
    } else {
      dispatch(markChatAsRead({ userInfluencerId: chatId, ...(influencerId && { influencerId }) }))
        .unwrap()
        .then(() => {
          dispatch(
            fetchInfluencerChat({
              userInfluencerId: chatId,
              ...(infId && { influencerId: infId }),
              refresh: true,
            }),
          )
            .unwrap()
            .then(() => {
              scrollToBottom();
            })
            .catch(() => {});
        });
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useSocketEvent(SOCKET_EVENTS.REFRESH_OPEN_CHAT, handleRefreshState);

  const onSelectMedia = media => {
    setBundleId(null);
    setIsAiSuggestion(false);
    remove();
    if (Array.isArray(media)) {
      if (media.length === 1) {
        if (media[0].caption) {
          setValue('message', media[0].caption);
        }
        if (media[0].cost) {
          setValue('price', media[0].cost);
        }
      }
      for (const m of media) {
        append(m);
      }
    } else {
      append(media);
      if (media.caption) {
        setValue('message', media.caption);
      }
      if (media.cost) {
        setValue('price', media.cost);
      }
    }
  };

  const manualChatToggle = () => {
    if (userInfluencer.totalSpent > 0) {
      toggleManualChat();
    } else {
      setSecretAction(SECRET_ACTION.AI_CHAT);
      secretModalRef.current.show();
    }
  };

  const onSecretConfirm = () => {
    if (secretAction === SECRET_ACTION.AI_CHAT) {
      toggleManualChat();
    } else {
      unblockUser();
    }
  };

  const toggleManualChat = () => {
    setManualChatEnabled(!manualChatEnabled);
    dispatch(toggleUserInfluencerManualChat({ userInfluencerId: chatId, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        secretModalRef.current.hide();
      })
      .catch(err => {
        notifyError(err.message);
        setManualChatEnabled(manualChatEnabled);
      });
  };

  const unblockUser = () => {
    dispatch(unblockUserInfluencerChat({ userInfluencerId: chatId, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        secretModalRef.current.hide();
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onSelectBundle = bundle => {
    setBundleId(null);
    setIsAiSuggestion(false);
    remove();
    dispatch(fetchBundleById({ bundleId: bundle.id, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(({ bundle }) => {
        setBundleId(bundle.id);
        if (bundle.representativeMedia) {
          setValue('freePreviewMedia', {
            file: { type: bundle.representativeMedia.type, path: bundle.representativeMedia.path },
            url: bundle.representativeMedia.url,
          });
        }
        if (bundle.caption) {
          setValue('message', bundle.caption);
        }
        if (bundle.cost) {
          setValue('price', bundle.cost);
        }
        for (const m of bundle.media) {
          append(m);
        }
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const handleRefCallback = node => {
    if (node) {
      setFooterRef(node);
    }
  };

  const onPinUser = () => {
    dispatch(pinUser({ userInfluencerId: chatId, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        handleRefreshMessagesState();
      });
  };

  const fetchAiSuggestions = manualFlowInstructionType => {
    dispatch(
      getAiChatSuggestions({
        userInfluencerId: chatId,
        manualFlowInstructionType,
        ...(influencerId && { influencerId }),
      }),
    )
      .unwrap()
      .then(({ suggestions }) => {
        setAiSuggestions(
          suggestions.map(({ text, speech }) => ({
            message: text,
            voiceBuffer: speech ? speech.data : null,
          })),
        );
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;

    const atBottom = container?.scrollHeight - container.scrollTop - container.clientHeight < 10;
    setIsAtBottom(atBottom);

    if (atBottom) {
      if (newMessagesCount) {
        setNewMessagesCount(0);
        handleRefreshState();
      }
    }
  };

  const audioRef = useRef(null);
  const aiSuggestionAudioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const setActiveVoiceBuffer = nextVoiceBuffer => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (aiSuggestionAudioRef.current) {
      aiSuggestionAudioRef.current.pause();
    }

    audioRef.current = null;
    aiSuggestionAudioRef.current = null;
    setIsPlaying(false);
    setVoiceBuffer(nextVoiceBuffer || null);
  };

  const playAiSuggestionVoice = suggestion => {
    const nextVoiceBuffer = suggestion.voiceBuffer;
    if (!nextVoiceBuffer) return;

    if (aiSuggestionAudioRef.current) {
      aiSuggestionAudioRef.current.pause();
    }

    aiSuggestionAudioRef.current = createAudioFromVoiceBuffer(nextVoiceBuffer);
    aiSuggestionAudioRef.current.play().catch(() => notifyError('Unable to play voice suggestion.'));
  };

  const onTestVoice = () => {
    const message = watch('message');
    if (!message.trim()) {
      notifyError('Please enter text');
      return;
    }
    setActiveVoiceBuffer(null);
    dispatch(testVoice({ message, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(({ speech }) => {
        setActiveVoiceBuffer(speech.data);
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const toggleVoice = () => {
    if (!audioRef.current) {
      audioRef.current = createAudioFromVoiceBuffer(voiceBuffer);

      audioRef.current.onplay = () => setIsPlaying(true);
      audioRef.current.onpause = () => setIsPlaying(false);
      audioRef.current.onended = () => setIsPlaying(false);
    }

    if (audioRef.current.paused) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  };

  if (!userInfluencer) {
    return;
  }

  return (
    <>
      <PageContainer>
        {pending ? (
          <LoaderContainer>
            <Spinner height={100} width={100} />
          </LoaderContainer>
        ) : (
          <>
            <Header $compact={isReviewer}>
              <HeaderColumn>
                <HeaderRow>
                  {smallScreen && (
                    <PinWrapper onClick={() => setShowContent(false)}>
                      <ChevronLeftIcon />
                    </PinWrapper>
                  )}
                  <Avatar>{getInitials(userInfluencer.displayName)}</Avatar>
                  <Text16Bold>{userInfluencer.displayName}</Text16Bold>
                  {!isReviewer && (
                    <PinWrapper onClick={() => editChatNameModalRef.current.show()}>
                      <EditIcon color="#1672BA" />
                    </PinWrapper>
                  )}
                </HeaderRow>
                <HeaderChatInfoRow>
                  <HeaderRow>
                    <CurrencyDollarIcon />
                    <HeaderText>Lifetime spent:</HeaderText>
                    <HeaderText>{formatCurrency(userInfluencer.totalSpent)}</HeaderText>
                  </HeaderRow>
                  <Line />
                  {userInfluencer.subscribedUntil && (
                    <>
                      <HeaderRow>
                        <HeaderText>Subscribed until:</HeaderText>
                        <HeaderText>
                          {new Date(userInfluencer.subscribedUntil).getFullYear() === 9999
                            ? 'Lifetime'
                            : format(new Date(userInfluencer.subscribedUntil), 'MMM d, yyyy')}
                        </HeaderText>
                      </HeaderRow>
                      <Line />
                    </>
                  )}
                  {userInfluencer.ignored && (
                    <>
                      <HeaderRow>
                        <HeaderTextBold>IGNORED</HeaderTextBold>
                      </HeaderRow>
                      <Line />
                    </>
                  )}
                  {!isReviewer && (
                    <PinWrapper disabled={pinUserPending} onClick={onPinUser}>
                      {userInfluencer.isPinned ? <PinIcon width={20} height={20} /> : <EmptyPinIcon />}
                    </PinWrapper>
                  )}
                </HeaderChatInfoRow>
              </HeaderColumn>
              <HeaderRow>
                {!isReviewer && (
                  <Switch
                    label="Manual Chat"
                    disabled={userInfluencer.chatDisabled}
                    onChange={manualChatToggle}
                    selectedValue={manualChatEnabled}
                  />
                )}
                {[CRM_ORGANIZATION_MEMBER_ROLE.OWNER, CRM_ORGANIZATION_MEMBER_ROLE.ADMINISTRATOR, CRM_ORGANIZATION_MEMBER_ROLE.TEAM_LEAD].includes(user.role) && (
                  <>
                    <AddToGroupButton onClick={() => addUserToCollectionModalRef.current.show()}>
                      <UserPlusIcon />
                      Add to
                    </AddToGroupButton>
                    <AddToGroupButton
                      onClick={() => {
                        setSecretAction(SECRET_ACTION.UNBLOCK);
                        secretModalRef.current.show();
                      }}>
                      Unstuck
                    </AddToGroupButton>
                  </>
                )}
              </HeaderRow>
            </Header>
            <Content ref={chatContainerRef} $footerHeight={footerHeight} $compact={isReviewer} onScroll={handleScroll}>
              {!!data.length && !timestamp && <InfiniteScrollMarker ref={loadMoreRef} />}
              <LoaderMoreContainer>{fetchMorePending && <Spinner width={50} height={50} />}</LoaderMoreContainer>
              {data.map((item, index) => {
                let isNewDay = false;
                if (item.timestamp) {
                  const currentDate = new Date(item.timestamp).toDateString();
                  const previousDate =
                    index > 0 && data[index - 1]?.timestamp ? new Date(data[index - 1].timestamp).toDateString() : null;
                  isNewDay = currentDate !== previousDate;
                }

                return (
                  <MessageRow key={item.id} id={item.id} $role={item.sender}>
                    {isNewDay && (
                      <DateDivider>
                        <DateDividerLine />
                        <Text14Light>{item.timestamp ? format(item.timestamp, 'MMM d, yyyy') : ''}</Text14Light>
                        <DateDividerLine />
                      </DateDivider>
                    )}
                    {item.target === MESSAGE_TARGET.BUNDLE && item.type === CHAT_ACTIVITY_TYPE.OFFER && (
                      <Bundle cost={item.cost} media={item.media} item={item} />
                    )}
                    {[MESSAGE_TARGET.PREMIUM_PHOTO, MESSAGE_TARGET.FREE_PHOTO].includes(item.target) && (
                      <MediaWrapper
                        onClick={() => {
                          setSelectedMedia({ url: item.url, kind: ALBUM_MEDIA_TYPE.PHOTO });
                          previewMediaModalRef.current.show();
                        }}>
                        <Image
                          src={item.thumbnailUrl}
                          onClick={() => {
                            setSelectedMedia({ url: item.url, kind: ALBUM_MEDIA_TYPE.VIDEO });
                            previewMediaModalRef.current.show();
                          }}
                        />
                        <MediaFooter $role={'INFLUENCER'}>
                          <TimeText $role={'INFLUENCER'} $purchased={item.purchased}>
                            {item.target === MESSAGE_TARGET.FREE_PHOTO
                              ? 'Free'
                              : `$${formatPrice(item.cost)} ${item.purchased ? 'paid' : 'not paid yet'}`}
                          </TimeText>
                          <DotText>•</DotText>
                          <TimeText $role={'INFLUENCER'}>
                            {item.timestamp ? format(item.timestamp, 'HH:mm') : ''}
                          </TimeText>
                        </MediaFooter>
                      </MediaWrapper>
                    )}
                    {[MESSAGE_TARGET.PREMIUM_VIDEO, MESSAGE_TARGET.FREE_VIDEO].includes(item.target) && (
                      <MediaWrapper
                        onClick={() => {
                          setSelectedMedia({ url: item.url, kind: ALBUM_MEDIA_TYPE.VIDEO });
                          previewMediaModalRef.current.show();
                        }}>
                        <Image
                          src={item.thumbnailUrl}
                          onClick={() => {
                            setSelectedMedia({ url: item.url, kind: ALBUM_MEDIA_TYPE.VIDEO });
                            previewMediaModalRef.current.show();
                          }}
                        />
                        <MediaFooter $role={'INFLUENCER'}>
                          <TimeText $role={'INFLUENCER'} $purchased={item.purchased}>
                            {item.target === MESSAGE_TARGET.FREE_VIDEO
                              ? 'Free'
                              : `$${formatPrice(item.cost)} ${item.purchased ? 'paid' : 'not paid yet'}`}
                          </TimeText>
                          <DotText>•</DotText>
                          <TimeText $role={'INFLUENCER'}>
                            {item.timestamp ? format(item.timestamp, 'HH:mm') : ''}
                          </TimeText>
                        </MediaFooter>
                        <PlayIconWrapper>
                          <PlayWhiteIcon />
                        </PlayIconWrapper>
                      </MediaWrapper>
                    )}
                    {[ALBUM_MEDIA_TYPE.PHOTO, ALBUM_MEDIA_TYPE.VIDEO].includes(item.type) && (
                      <MediaWrapper
                        $role={item.sender}
                        onClick={() => {
                          setSelectedMedia({ url: item.url, kind: item.type });
                          previewMediaModalRef.current.show();
                        }}>
                        {item.type === ALBUM_MEDIA_TYPE.PHOTO ? (
                          <Image
                            src={item.url}
                            onClick={() => {
                              setSelectedMedia({ url: item.url, kind: item.type });
                              previewMediaModalRef.current.show();
                            }}
                          />
                        ) : (
                          <>
                            <Video
                              src={item.url}
                              onClick={() => {
                                setSelectedMedia({ url: item.url, kind: item.type });
                                previewMediaModalRef.current.show();
                              }}
                            />
                            <PlayIconWrapper>
                              <PlayWhiteIcon />
                            </PlayIconWrapper>
                          </>
                        )}
                        <MediaFooter>
                          <TimeText $role={item.sender}>
                            {item.timestamp ? format(item.timestamp, 'HH:mm') : ''}
                          </TimeText>
                        </MediaFooter>
                      </MediaWrapper>
                    )}
                    {item.type === MESSAGE_TYPE.MESSAGE && (
                      <MessageBubbleWrapper $role={item.sender}>
                        {canSeeChatterName && item.chatter?.name && (
                          <ChatterAvatarWrapper>
                            <ChatterAvatar>{item.chatter.name.charAt(0)}</ChatterAvatar>
                            <ChatterAvatarTooltip>{item.chatter.name}</ChatterAvatarTooltip>
                          </ChatterAvatarWrapper>
                        )}
                        <MessageBubble
                          $role={item.sender}
                          $pending={item.pending}
                          $isAudio={item.format === 'VOICE'}
                          $highlighted={!!interactionId && item.id === `received-${interactionId}`}>
                          {item.pending && <MessageSpinner src={SpinnerGIF} />}
                          {item.message}
                          {item.format === 'VOICE' && (
                            <VoiceResponseText $role={item.sender}>{`<audio>`}</VoiceResponseText>
                          )}
                          <MessageTimeText $role={item.sender}>
                            {item.timestamp ? format(item.timestamp, 'HH:mm') : ''}
                          </MessageTimeText>
                        </MessageBubble>
                        {isReviewer && item.chatter && item.flow === MESSAGE_FLOW.MANUAL_FLOW && (
                          <AlertButton
                            type="button"
                            onClick={() => {
                              setReportedInteractionId(item.id.split('-')[1]);
                              reportMessageModalRef.current.show();
                            }}>
                            <AlertHexagonIcon />
                          </AlertButton>
                        )}
                      </MessageBubbleWrapper>
                    )}
                    {item.type === CHAT_ACTIVITY_TYPE.PURCHASE && (
                      <CenteredBubble
                        id={item.id}
                        type={item.type}
                        cost={item.cost}
                        url={item.thumbnailUrl}
                        target={item.target}
                      />
                    )}
                    {item.type === CHAT_ACTIVITY_TYPE.OFFER && item.target === MESSAGE_TARGET.SUBSCRIPTION && (
                      <CenteredBubble id={item.id} type={item.type} cost={item.cost} target={item.target} />
                    )}
                    {item.type === CHAT_ACTIVITY_TYPE.EVENT &&
                      item.target === MESSAGE_TARGET.SWITCH_CONVERSATION_PHASE && (
                        <CenteredBubble
                          id={item.id}
                          type={item.type}
                          cost={item.cost}
                          target={item.target}
                          metadata={item.metadata}
                          timestamp={item.timestamp}
                        />
                      )}
                  </MessageRow>
                );
              })}
            </Content>
            {!!newMessagesCount && (
              <NewMessagesContainer onClick={() => scrollToBottom(true)} $footerHeight={footerHeight}>
                <Text12ExtraBold>
                  {newMessagesCount} new message{newMessagesCount > 1 ? 's' : ''}
                </Text12ExtraBold>
              </NewMessagesContainer>
            )}
            {!isReviewer && (
              <Footer
                ref={handleRefCallback}
                autoComplete="off"
                onSubmit={handleSubmit(data => sendChatMessage(data, false))}>
                {aiSuggestions && (
                  <AiSuggestions
                    suggestions={aiSuggestions}
                    setSuggestions={setAiSuggestions}
                    onPlaySuggestion={playAiSuggestionVoice}
                    onSelectSuggestion={suggestion => {
                      if (Object.prototype.hasOwnProperty.call(suggestion, 'voiceBuffer')) {
                        setActiveVoiceBuffer(suggestion.voiceBuffer);
                      }

                      if (suggestion.isOriginal) {
                        setIsAiSuggestion(false);
                        setValue('message', suggestion.message);
                        handleSubmit(data => sendChatMessage(data, true))();
                      } else {
                        setIsAiSuggestion(true);
                        setValue('message', suggestion.message);
                      }
                    }}
                  />
                )}
                {!!fields.length && (
                  <SelectedMediaContainer>
                    {!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE) &&
                      (freePreviewMediaWatch ? (
                        <MediaItem
                          type={
                            freePreviewMediaWatch.file.type.toLowerCase().includes('video')
                              ? ALBUM_MEDIA_TYPE.VIDEO
                              : ALBUM_MEDIA_TYPE.PHOTO
                          }
                          file={freePreviewMediaWatch}
                          progress={progress}
                          onDelete={() => {
                            setValue('freePreviewMedia', null);
                            setUploadErrors(prev =>
                              prev.filter(error => error.fileName !== freePreviewMediaWatch.file.name),
                            );
                          }}
                          uploadError={uploadErrors.find(error => error.fileName === freePreviewMediaWatch.file.name)}
                        />
                      ) : (
                        <Controller
                          name="freePreviewMedia"
                          control={control}
                          render={({ field }) => {
                            return (
                              <UploadMediaButtonAlt
                                type={UPLOAD_FILE_CONSTRAINTS.TYPE.MIXED}
                                onChange={files => field.onChange(files[0])}
                                setError={setUploadErrors}
                                multiple={false}
                                title="Add Free Preview"
                              />
                            );
                          }}
                        />
                      ))}
                    {fields.map((field, index) => {
                      return (
                        <MediaItem
                          key={field.id}
                          kind={field.kind}
                          type={field.kind}
                          onClick={() => {
                            setSelectedMedia(field);
                            previewMediaModalRef.current.show();
                          }}
                          file={field}
                          onDelete={
                            bundleId
                              ? null
                              : e => {
                                  if (fields.length === 1) {
                                    setValue('mediaTags', []);
                                    setValue('price', '');
                                    setValue('message', '');
                                  }
                                  remove(index);
                                  e.stopPropagation();
                                }
                          }
                        />
                      );
                    })}
                  </SelectedMediaContainer>
                )}
                {fields.length > 1 && (
                  <ClearMediaText
                    onClick={() => {
                      remove();
                      setValue('mediaTags', []);
                      setBundleId(null);
                    }}>
                    Clear all
                  </ClearMediaText>
                )}
                <FooterContent>
                  <UploadChatMediaButton
                    onClick={tags => {
                      setIsAiSuggestion(false);
                      reset();
                      if (Array.isArray(tags)) {
                        setValue('mediaTags', tags);
                        selectMediaModalRef.current.show();
                      } else {
                        setValue('mediaTags', [MEDIA_ASSET_SALE_TAG.SINGLE_PAID]);
                        setReload(true);
                        selectBundleModalRef.current.show();
                      }
                    }}
                  />
                  <Input
                    placeholder="Type a Message"
                    {...register('message')}
                    type="textarea"
                    textAreaHeight={70}
                    error={
                      errors.message?.message ||
                      errors.price?.message ||
                      errors.media?.message ||
                      errors.media?.root?.message
                    }
                  />
                  {mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID) && !!fields.length && (
                    <PriceInput leftIcon="$" placeholder="Price" {...register('price')} />
                  )}
                  <AiSuggestionsButtonWrapper>
                    <FooterButton type="button" disabled={getAiChatSuggestionsPending}>
                      <StarsIcon />
                    </FooterButton>
                    <AiSuggestionsMenu>
                      {Object.values(MANUAL_FLOW_INSTRUCTION_TYPE).map(type => (
                        <AiSuggestionsMenuItem
                          key={type}
                          type="button"
                          disabled={getAiChatSuggestionsPending}
                          onClick={() => fetchAiSuggestions(type)}>
                          {type}
                        </AiSuggestionsMenuItem>
                      ))}
                    </AiSuggestionsMenu>
                  </AiSuggestionsButtonWrapper>
                  {!!voiceBuffer && (
                    <FooterButton type="button" disabled={testVoicePending} onClick={toggleVoice}>
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </FooterButton>
                  )}
                  {(!fields.length || userInfluencer.enabledVoice) && (
                    <FooterButton
                      type="button"
                      disabled={testVoicePending}
                      onClick={onTestVoice}
                      $active={!!voiceBuffer}>
                      {voiceBuffer ? (
                        <RefreshIcon color={!!voiceBuffer ? '#fff' : '#1672BA'} />
                      ) : (
                        <MicrophoneIcon color={!!voiceBuffer ? '#fff' : '#1672BA'} />
                      )}
                      {!!voiceBuffer && (
                        <CloseButton
                          onClick={e => {
                            setActiveVoiceBuffer(null);
                            e.stopPropagation();
                          }}>
                          <CloseIcon color="#fff" width={12} height={12} />
                        </CloseButton>
                      )}
                    </FooterButton>
                  )}
                  {fields.length > 1 && (
                    <Controller
                      name="isVip"
                      control={control}
                      render={({ field }) => {
                        return (
                          <VipButton type="button" onClick={() => field.onChange(!field.value)}>
                            {field.value ? <CheckIcon color="#1672BA" /> : <CheckEmptyIcon color="#1672BA" />}
                            VIP
                          </VipButton>
                        );
                      }}
                    />
                  )}
                  <FooterButton disabled={userInfluencer.chatDisabled || sendingMessage}>
                    <SendIcon />
                  </FooterButton>
                </FooterContent>
              </Footer>
            )}
          </>
        )}
      </PageContainer>
      <SelectMediaModal
        modalRef={selectMediaModalRef}
        userInfluencerId={chatId}
        influencerId={influencerId}
        tag={mediaTags}
        onAdd={onSelectMedia}
        multiple={!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE)}
        maxSelectedItems={10}
        exactMatch={!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)}
        onClose={() => {
          if (!fields.length) {
            setValue('mediaTags', []);
          }
        }}
      />
      <SelectBundleModal
        modalRef={selectBundleModalRef}
        userInfluencerId={chatId}
        influencerId={influencerId}
        onAdd={onSelectBundle}
        reload={reload}
        setReload={setReload}
        onClose={() => {
          if (!fields.length) {
            setValue('mediaTags', []);
          }
        }}
      />
      <EditChatDisplayNameModal
        modalRef={editChatNameModalRef}
        userInfluencerId={chatId}
        influencerId={influencerId}
        onSuccess={() => {
          handleRefreshMessagesState();
        }}
      />
      <AddUserToCollectionModal
        modalRef={addUserToCollectionModalRef}
        userInfluencerId={chatId}
        influencerId={influencerId}
      />
      <PreviewAlbumMediaModal
        modalRef={previewMediaModalRef}
        url={selectedMedia?.url}
        type={selectedMedia?.kind}
        setSelectedUrl={setSelectedMedia}
      />
      <SecretModal
        ref={secretModalRef}
        loading={toggleUserInfluencerManualChatPending || unblockUserInfluencerChatPending}
        onConfirm={onSecretConfirm}
      />
      <ReportMessageModal modalRef={reportMessageModalRef} interactionId={reportedInteractionId} />
    </>
  );
};

export default ChatPage;
